// src/components/Sidebar.jsx
import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  FaMoneyCheckAlt,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaHome,
  FaPaw,
  FaStethoscope,
  FaUsers,
  FaCalendarPlus,
  FaCalendarAlt,
  FaUser,
  FaChevronRight,
  FaClosedCaptioning,
} from "react-icons/fa";
import { FaUserDoctor } from "react-icons/fa6";
import { useEffect } from "react";

import logo from "./../assets/logo.png";
import { Heart, Stethoscope } from "lucide-react";
import { use } from "react";

const Sidebar = () => {
  const { loginUser, logout } = useContext(AuthContext);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const { userDetails, getUserDetails } = useContext(AuthContext);

  const { doctorDetails, getDoctorDetails } = useContext(AuthContext);

  useEffect(() => {
    if (userDetails == null || Object.keys(userDetails).length === 0) {
      getUserDetails();
    }
  }, [getUserDetails, userDetails]);

  const handleLogout = () => {
    logout();
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const navigation = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: <FaHome />,
      roles: ["admin", "client", "doctor"],
      color: "text-blue-400",
    },
    {
      name: "My Pets",
      path: "/dashboard/my-pets",
      icon: <FaPaw />,
      roles: ["client"],
      color: "text-green-400",
    },
    {
      name: "Self-Checkup",
      path: "/dashboard/self-checkup",
      icon: <FaStethoscope />,
      roles: ["client"],
      color: "text-purple-400",
    },
    {
      name: "All Pets",
      path: "/dashboard/all-pets",
      icon: <FaPaw />,
      roles: ["admin"],
      color: "text-green-400",
    },
    {
      name: "All Users",
      path: "/dashboard/all-user",
      icon: <FaUsers />,
      roles: ["admin"],
      color: "text-orange-400",
    },
    {
      name: "Create Appointment",
      path: "/dashboard/create-appointment",
      icon: <FaCalendarPlus />,
      roles: ["client"],
      color: "text-indigo-400",
    },
    {
      name: "My Appointments",
      path: "/dashboard/my-appointment",
      icon: <FaCalendarAlt />,
      roles: ["client"],
      color: "text-pink-400",
    },
    {
      name: "All Appointments",
      path: "/dashboard/all-appointment",
      icon: <FaCalendarAlt />,
      roles: ["admin", "doctor"],
      color: "text-pink-400",
    },
    {
      name: "CheckUp",
      path: "/dashboard/check-up",
      icon: <Stethoscope />,
      roles: ["doctor"],
      color: "text-green-400",
    },
    {
      name: "Profile",
      path: "/dashboard/profile",
      icon: <FaUser />,
      roles: ["doctor", "client"],
      color: "text-yellow-400",
    },
    // {
    //   name: "Online Consultation",
    //   path: "/dashboard/meeting",
    //   icon: <Stethoscope />,
    //   roles: ["doctor"],
    //   color: "text-green-400",
    // },
  ];

  const filteredNavigation = navigation.filter((item) =>
    item.roles.includes(loginUser?.role)
  );

  const isActive = (path) => location.pathname === path;

  const getRoleDisplayName = (role) => {
    switch (role) {
      case "client":
        return "Pet Parent";
      case "doctor":
        return "Doctor";
      case "admin":
        return "Admin";
      default:
        return role;
    }
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-4 shadow-lg backdrop-blur-sm">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-gray-100 rounded-full overflow-hidden">
            <img
              src={userDetails?.userDetails?.avatar}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <button
          onClick={toggleSidebar}
          aria-label="Toggle Sidebar"
          className="relative p-2 rounded-lg hover:bg-slate-700 transition-colors duration-200 z-[70]"
        >
          <div className="relative w-5 h-5 flex items-center justify-center">
            {isOpen ? (
              <FaTimes className="w-5 h-5 text-white transform transition-transform duration-200 hover:rotate-90" />
            ) : (
              <FaBars className="w-5 h-5 text-white transition-transform duration-200 hover:scale-110" />
            )}
          </div>
        </button>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-20 md:hidden transition-opacity duration-300"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col shadow-2xl transform ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } transition-all duration-300 ease-in-out z-50 md:translate-x-0 md:static md:shadow-xl backdrop-blur-lg border-r border-slate-700/50`}
      >
        {/* Logo / Title */}
        <div className="p-4 border-b border-slate-700/50 bg-slate-800/50">
          <img src={logo} alt="logo" className="" />
        </div>

        {/* User Profile Section */}
        <div className="p-6 border-b border-slate-700/50 bg-slate-800/30">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gray-100 rounded-full overflow-hidden">
              <img
                src={userDetails?.userDetails?.avatar}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">
                {(loginUser?.role == "doctor"
                  ? doctorDetails?.name
                  : userDetails?.userDetails?.name) || "User Name"}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <FaUserDoctor className="text-blue-400 text-xs" />
                <span className="text-slate-300 text-xs capitalize">
                  {getRoleDisplayName(loginUser?.role)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {filteredNavigation.map((item, index) => (
              <li key={item.name}>
                <Link
                  to={item.path}
                  className={`group flex items-center gap-4 p-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive(item.path)
                      ? "bg-gradient-to-r from-blue-600/90 to-purple-600/90 text-white shadow-lg shadow-blue-500/25 scale-105"
                      : "text-slate-300 hover:bg-slate-700/50 hover:text-white hover:scale-105 hover:shadow-lg"
                  }`}
                  onClick={() => setIsOpen(false)}
                  aria-current={isActive(item.path) ? "page" : undefined}
                >
                  <span
                    className={`text-lg transition-colors duration-200 ${
                      isActive(item.path) ? "text-white" : item.color
                    }`}
                  >
                    {item.icon}
                  </span>
                  <span className="flex-1">{item.name}</span>
                  {isActive(item.path) && (
                    <FaChevronRight className="text-xs opacity-70" />
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-slate-700/50 bg-slate-800/30">
          <button
            onClick={() => {
              handleLogout();
              setIsOpen(false);
            }}
            className="w-full group flex items-center gap-4 p-3 text-sm font-medium rounded-xl text-slate-300 hover:bg-red-600/20 hover:text-red-400 focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-all duration-200 hover:scale-105"
            aria-label="Logout"
          >
            <FaSignOutAlt className="text-lg group-hover:rotate-12 transition-transform duration-200" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
